/* eslint-disable tailwindcss/no-arbitrary-value */
import { ApplyCtaLink } from '@hyphengroup/cms-components/components/ApplyCta';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import classNames from 'classnames';
import { FC } from 'react';
import { ProductHeroKeyStat } from '../../productHero';
import { FlexibleHeroBlockStkBkaKeyStats } from './FlexibleHeroBlockStkBkaKeyStats';
import { FlexibleHeroBlockStkBkaPromoCode } from './FlexibleHeroBlockStkBkaPromoCode';
import { ComponentFlexibleHeroBlock } from '$/(cms)/_modules/cmsService/types';
import Button from '$/components/Button';

type FlexibleHeroBlockStkBkaSideCardProps = {
  logo?: { src: string; alt: string };
  promoCode?: string;
  product: NonNullable<ComponentFlexibleHeroBlock['product']>;
  /** The Apply CTA's label; present only when the CTA resolves to a URL. */
  ctaLabel?: string;
  /**
   * A CTA authored on the block, resolved instead of the product's own URL
   * (which ApplyCtaLink finds itself).
   */
  ctaLinkOverride?: string;
  onApplyClick: () => void;
  keyStats: ProductHeroKeyStat[];
  lang: string;
  className?: string;
};

/**
 * The product card beside (desktop) or under (mobile) the STK/BKA hero text:
 * logo and promo code, the Apply CTA and the key stats.
 */
export const FlexibleHeroBlockStkBkaSideCard: FC<
  FlexibleHeroBlockStkBkaSideCardProps
> = ({
  logo,
  promoCode,
  product,
  ctaLabel,
  ctaLinkOverride,
  onApplyClick,
  keyStats,
  lang,
  className,
}) => (
  <div
    className={classNames(
      'flex w-full flex-col gap-spacing/gap/soft-12px rounded-radius/card px-spacing/padding/x py-spacing/padding/y shadow-[0_4px_24px_0_rgba(0,0,0,0.1)] bg-fill/card',
      className,
    )}
    data-component="flexible-hero-block-stk-bka-side-card"
  >
    {(logo || promoCode) && (
      <div className="flex w-full items-start justify-between gap-spacing/gap/soft-12px">
        {logo && (
          // Fixed logo plate: the box reserves its size before the image
          // loads, whatever the uploaded logo's own proportions (no CLS).
          <div className="flex h-[54px] w-25 shrink-0 items-center justify-center rounded-radius/card-inner border border-borders/default px-spacing/gap/x-sm">
            <CmsImage
              src={logo.src}
              alt={logo.alt}
              width={66}
              height={40}
              // The plate is 100px wide on every viewport; its padding only
              // narrows it, so this is the upper bound of the image slot.
              sizes={{ _: 100 }}
              className="h-auto max-h-full w-full object-contain"
            />
          </div>
        )}
        {promoCode && (
          <FlexibleHeroBlockStkBkaPromoCode promoCode={promoCode} lang={lang} />
        )}
      </div>
    )}

    {ctaLabel && (
      // The click handler is on the link: Enter on the focused link reaches it
      // too, and a click on the Button bubbles there.
      <ApplyCtaLink
        product={product}
        ctaLinkOverride={ctaLinkOverride}
        className="w-full"
        onClick={onApplyClick}
      >
        <Button variant="filled" styleType="cta" className="w-full">
          {ctaLabel}
        </Button>
      </ApplyCtaLink>
    )}

    {keyStats.length > 0 && (
      <FlexibleHeroBlockStkBkaKeyStats keyStats={keyStats} />
    )}
  </div>
);
